import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type { ReactNode } from 'react';

interface SectionProps {
  id?: string;
  gradient?: boolean;
  header?: string;
  subtitle?: string;
  className?: string;
  /**
   * Supply the page container here instead of relying on the renderer.
   *
   * A widget rendered through `SlotRenderer` gets it from the block wrapper
   * (70.14-Widget-Width), so it leaves this off — the default. Only a caller
   * that renders a `Section` *outside* the block pipeline needs it, which today
   * is the `/demo` scratch route's hand-written sections.
   */
  container?: boolean;
  children: ReactNode;
}

export const Section = ({
  id,
  gradient,
  header,
  subtitle,
  className,
  container = false,
  children,
}: SectionProps) => (
  <section
    id={id}
    className={classNames(
      'pt-4',
      gradient && 'section-fade',
      id && 'scroll-mt-20',
      className,
    )}
  >
    {/* Normally no container of its own: the SlotRenderer wrapper supplies the
        page measure and gutter (70.14-Widget-Width), and this div is only the
        heading/subtitle/body grouping. A caller outside the block pipeline
        asks for the container explicitly. */}
    <div className={container ? 'mx-auto max-w-6xl px-4 lg:px-9' : undefined}>
      {header && (
        // An eyebrow, not a heading (70.15-Heading-Structure).
        <p className="h5 text-primary text-center text-sm font-bold tracking-[0.10em] uppercase md:text-base">
          {header}
        </p>
      )}
      {/* Gaps are top padding on the element that needs them, never bottom
          margin on the one before it (70.12-Widget-Spacing). The body's
          `pt-4` is what the subtitle's `mb-4 md:mb-10` used to be; it only
          applies when there is a heading above it to be spaced from. */}
      {subtitle && (
        <h2 className={classNames('text-center', header && 'pt-3')}>
          {subtitle}
        </h2>
      )}
      <div className={classNames('space-y-3', (header || subtitle) && 'pt-4')}>
        {children}
      </div>
    </div>
  </section>
);
